
import {
  Component,
  OnInit,
  Input,
  ViewContainerRef,
  ViewEncapsulation
} from '@angular/core';
import {Subscription} from 'rxjs/Subscription';
import { Router, ActivatedRoute, ParamMap, Params, NavigationStart, NavigationEnd } from '@angular/router';

import { RequestsServices } from '../../../services/requests.service';
import { UserService } from '../../../services/user.service';
import { StorageService } from '../../../services/storage.service';
import { PageBase } from '../../../page.base';
import { ToasterService } from '../../../services/toaster.service';
import { TranslateService } from '@ngx-translate/core';
import { HeaderMenuComponent } from '../../../components/header-menu/header-menu.component';
import { marker, icon, latLng, Layer, tileLayer } from 'leaflet';


@Component({
  selector: 'app-company-detail',
  templateUrl: './company-detail.component.html',
  providers: [ RequestsServices, StorageService, UserService ],
  encapsulation: ViewEncapsulation.None
})

export class CompanyDetailComponent implements OnInit {

  public isLoading: boolean;

  private routeParamsSubscription: Subscription;
  public typeBusiness;
  public company;
  public companyAccounts = 0;
  public establishment = {offer: {title: '', term: ''}};
  public displayAllAddressesFlag = false;
  public displayMapFlag = false;
  public showShareButtons = false;
  public isHidden = false;
  public referenceAddress;

  @Input() leafletMarkers;

  public markers: Layer[] = [];

  LAYER_OSM = tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', { maxZoom: 18, attribution: 'Open Street Map' });
  public location = {
    lat: -3.7962083,
    lng: -38.6261701
  };
  public options = {
    layers: [ this.LAYER_OSM ],
    zoom: 10,
    center: latLng(this.location.lat, this.location.lng)
  };

  constructor(private translate: TranslateService,
            private toaster: ToasterService,
            private request: RequestsServices,
            private route: ActivatedRoute,
            private router: Router,
            private storage: StorageService,
            private userService: UserService) {

    const loggedAccount = this.userService.getLoggedAccount();
    this.typeBusiness = this.userService.getTypeAccount();

    let languageCurrent = navigator.language;
    translate.addLangs(['en', 'pt-br']);
    translate.setDefaultLang('en');
    languageCurrent === 'pt-BR' ? translate.use('pt-br') : translate.use('en');

  }

  public ngOnInit(): void {
    window.scrollTo(0, 0);

    const establishmentAccount = this.userService.getLoggedAccount();
    const typeBusiness = this.userService.getTypeAccount();
    if (typeBusiness === 'establishment' && establishmentAccount) {
      this.getEstablishment(establishmentAccount['id']);
    } else {
      this.toaster.error('Aviso!', 'Conta de negócio inválida', false, 4000);
      this.router.navigate(['/home']);
    }

    if (this.route.snapshot.params.companyId) {
      const companyId = this.route.snapshot.params.companyId;

      if (this.route.snapshot.queryParams && this.route.snapshot.queryParams.address) {
        const addressId = this.route.snapshot.queryParams.address;
        this.getReferenceAddress(addressId);
      } else {
        this.getCompany(companyId);
      }

    }
  }

  public getUserLevel(): string {
    return this.userService.getUserLevel();
  }

  public addMarker(lat, lon, addresObject) {
    const image = (this.company && this.company['image']) ? this.company['image'] : '';
    const name = (this.company && this.company['name']) ? this.company['name'] : '';
    const address = this.getAddressAsString(addresObject);
    const mapCard = `
      <div>
        <div class="card-wrapper">
          <div class="image-card" style="background-image: url('${image}')"></div>
          <div class="card-content">
            <div class="card-content-wrapper">
              <h2>${name}</h2>
              <p>${address}</p>
            </div>
          </div>
        </div>
      </div>
    `;

    const newMarker = marker(
      [ lat, lon ],
      {
        icon: icon({
          iconSize: [ 50, 50 ],
          iconAnchor: [ 13, 41 ],
          iconUrl: 'assets/icon/marker-allya.svg',
          shadowUrl: ''
        })
      }
    ).bindPopup(mapCard).openPopup();

    this.markers.push(newMarker);
  }

  public removeMarker() {
    this.markers.pop();
  }

  public getAddressAsString(addressObject: Object): string {
    const street = addressObject['street'];
    const number = addressObject['number'];
    const neighborhood = addressObject['neighborhood'];
    const county = addressObject['county'];
    const state = addressObject['state'];
    const cep = addressObject['cep'];
    return street + ', ' + number + ', ' + neighborhood + ' - ' + county + '/' + state + ' - ' + cep;
  }

  private getReferenceAddress(addressId): any {
    this.request.get({path: 'company_addresses', pathParam: `/${addressId}`}).then((address) => {
      this.referenceAddress = address;
      this.getCompany(address.companyId, address);
    });
  }

  private getCompany(companyId, addressReference?): void {

    const filterObject: any = {
              include: {
                relation: 'addresses'
              }
    };

    // Check if was set an address as reference on URL. If so, get addresses ordered by proximity
    if (addressReference && addressReference.location && addressReference.location.lat && addressReference.location.lng) {
      const addressReferenceLocation = {lat: addressReference.location.lat, lng: addressReference.location.lng};
      // This will bring the addresses ordered by proximity
      filterObject.include = {...filterObject.include, scope: { where: {location: {near: addressReferenceLocation}} }};
    }

    const createParameters = JSON.stringify(filterObject);

    this.isLoading = true;
    this.request.get({path: 'companies', pathParam: `/${companyId}?filter=${createParameters}`}).then((company) => {
      this.isLoading = false;
      this.company = company;
      this.initCompanyDetail();
    });

    this.isLoading = true;
    this.request.get({path: 'companies', pathParam: `/${companyId}/accounts/count`}).then((res) => {
      this.isLoading = false;
      this.companyAccounts = res.count;
    });
  }

  private getEstablishment(establishmentId): void {
    this.isLoading = true;
    this.request.get({path: 'establishments', pathParam: `/${establishmentId}`}).then((establishment) => {
      this.isLoading = false;
      this.establishment = establishment;
    });
  }

  private initCompanyDetail(): void {
    this.initMapMarkers();
  }

  public initMapMarkers(): void {
    if (this.company && this.company.addresses.length > 0) {
      for (const address of this.company.addresses) {
        if (address.location && address.location.lat && address.location.lng) {
          this.addMarker(address.location.lat, address.location.lng, address);
          this.options = {
            layers: [ this.LAYER_OSM ],
            zoom: 10,
            center: latLng(address.location.lat, address.location.lng)
          };
        }
      }
    }
  }

  public toggleShowShareButtons(): void {
    if (this.showShareButtons) {
      this.showShareButtons = false;
    } else {
      this.showShareButtons = true;
    }
  }

  public displayAllAddresses(): void {
    if (this.displayAllAddressesFlag) {
      this.displayAllAddressesFlag = false;
    } else {
      this.displayAllAddressesFlag = true;
    }
  }

  public canDisplayAllAddresses(): boolean {
    return this.displayAllAddressesFlag;
  }

  public displayMap(): void {
    if (this.displayMapFlag) {
      this.displayMapFlag = false;
    } else {
      this.displayMapFlag = true;
    }
  }

  public canDisplayMap(): boolean {
    return this.displayMapFlag;
  }

}
